/**
 * SIMPLE ACCESS CONTROL FOR LIMEPHONE
 * 
 * Simple approach: Check account status + plan for feature access
 * Account status (trial, active, suspended) + Plan (starter, growth, enterprise)
 */

// Plan types - includes both legacy and current plan names
export type PlanType = 
  | 'trial' 
  | 'free'
  | 'lite'
  | 'starter'
  | 'growth' 
  // Current Stripe plan IDs
  | 'lite-plan-monthly'
  | 'lite-plan-annual'
  | 'starter-plan-monthly'
  | 'starter-plan-annual'
  | 'growth-plan-monthly'
  | 'growth-plan-annual'
  // Legacy values
  | 'active'
  | 'enterprise'
  | 'enterprise-plan-monthly'
  | 'enterprise-plan-annual';

// Account status types (from schema)
export type AccountStatus = 'trialing' | 'active' | 'canceled' | 'archived';
export type AccessPosture = 'normal' | 'restricted' | 'locked' | 'suspended';

// Feature list
export type Feature =
  | 'sms'
  | 'voice'
  | 'aiVoice'
  | 'phoneNumbers'
  | 'campaigns'
  | 'analytics'
  | 'integrations'
  | 'api'
  | 'teamManagement'
  // Paid caller-reputation lookup (Nomorobo via Twilio Lookup) on inbound
  // calls. Starter and Growth.
  | 'spamLookup'
  // Outbound-brand features. The backend ships these for every org; they are
  // gated purely by plan membership below. LimePhone plans omit all of them
  // EXCEPT powerDialer, which Growth includes, so the same codebase serves both
  // brands with no per-brand forks.
  | 'skipTrace'
  | 'powerDialer'
  | 'predictiveDialer'
  | 'voicemailDrop'
  | 'dripCampaigns'
  | 'aiCompliance'
  | 'multiProvider'
  // Outbound answering-machine detection (AMD_ENABLED kill switch too) and the
  // iPhone call-screening assist (auto-detect + auto-introduce). Starter and
  // Growth; Twilio bills AMD per call. Voicemail drop rides on AMD, so it is
  // on the same tiers.
  | 'answeringMachineDetection'
  | 'callScreeningAssist'
  // "Hold for me": live-transcribed hold watch on an outbound call that pings
  // the user when a person picks up. Starter and Growth (transcription is billed).
  | 'holdForMe';

// Define feature sets by tier
const TRIAL_FEATURES: Feature[] = ['sms', 'voice'];
const FREE_FEATURES: Feature[] = ['sms', 'voice'];
const LITE_FEATURES: Feature[] = ['sms', 'voice', 'phoneNumbers'];
const STARTER_FEATURES: Feature[] = ['sms', 'voice', 'aiVoice', 'phoneNumbers', 'campaigns', 'analytics', 'teamManagement', 'spamLookup', 'answeringMachineDetection', 'callScreeningAssist', 'voicemailDrop', 'holdForMe'];
// Growth is the top LimePhone tier, and the power dialer is its headline
// outbound feature. It is the ONE outbound-brand module a LimePhone plan
// includes -- the rest stay outbound-only.
const GROWTH_FEATURES: Feature[] = ['sms', 'voice', 'aiVoice', 'phoneNumbers', 'campaigns', 'analytics', 'integrations', 'api', 'teamManagement', 'powerDialer', 'spamLookup', 'answeringMachineDetection', 'callScreeningAssist', 'voicemailDrop', 'holdForMe'];

// Outbound brand (REI/wholesaler) tiers. Inherit the core comms features and
// add the outbound-only modules. predictiveDialer is reserved for the top tier.
const OUTBOUND_CORE: Feature[] = [
  'sms', 'voice', 'phoneNumbers', 'campaigns', 'analytics', 'teamManagement',
  'skipTrace', 'voicemailDrop', 'dripCampaigns', 'aiCompliance', 'multiProvider',
  'answeringMachineDetection', 'callScreeningAssist',
];
const OUTBOUND_STARTER_FEATURES: Feature[] = [...OUTBOUND_CORE];
const OUTBOUND_PRO_FEATURES: Feature[] = [...OUTBOUND_CORE, 'powerDialer', 'integrations'];
const OUTBOUND_ELITE_FEATURES: Feature[] = [...OUTBOUND_CORE, 'powerDialer', 'predictiveDialer', 'integrations', 'api', 'aiVoice'];

// Plan-to-features mapping (for active accounts) - covers all plan variations
export const PLAN_FEATURES: Record<string, Feature[]> = {
  // Trial/Free tiers
  trial: TRIAL_FEATURES,
  free: FREE_FEATURES,
  
  // Lite/Personal tier (Light plan). Bare `lite` is what planTierFromPlanId
  // writes for a Lite subscription, like `starter` and `growth` below.
  lite: LITE_FEATURES,
  'lite-plan-monthly': LITE_FEATURES,
  'lite-plan-annual': LITE_FEATURES,
  
  // Starter tier
  starter: STARTER_FEATURES,
  'starter-plan-monthly': STARTER_FEATURES,
  'starter-plan-annual': STARTER_FEATURES,
  
  // Growth tier
  //
  // Bare `growth` is a pre-Stripe-id value and is NOT product-scoped on
  // purpose. Measured on production 2026-09-12: three organisations carry it,
  // all three are product='limecall', and every member of all three is one of
  // our own staff accounts -- no third-party customer is on it. (To re-check:
  // join organizations on organization_members/users where plan_type='growth'.)
  // Restricting it to product='limephone' therefore revokes nothing but our
  // own access -- including the org used to test /dialer -- while risking a
  // legacy LimePhone Growth customer if one ever lands here. Leave it broad.
  growth: GROWTH_FEATURES,
  'growth-plan-monthly': GROWTH_FEATURES,
  'growth-plan-annual': GROWTH_FEATURES,
  
  // Enterprise tier (Merged into growth for now or legacy)
  enterprise: GROWTH_FEATURES,
  'enterprise-plan-monthly': GROWTH_FEATURES,
  'enterprise-plan-annual': GROWTH_FEATURES,
  
  // Legacy value - treat as starter
  active: STARTER_FEATURES,

  /*
   * ===== Legacy ANNUAL ids — the names are wrong, deliberately =====
   *
   * These come from an older lineup and do NOT mean what they say. The
   * catalogue's own metadata (and server/lib/planIntervals.test.ts,
   * server/lib/defaultAiPools.ts) pin the real mapping:
   *
   *   starter_yearly  = Lite    ($90/yr,  monthlyPlanId lite-plan-monthly)
   *   growth_yearly   = Starter ($180/yr, monthlyPlanId starter-plan-monthly)
   *   business_yearly = Growth  ($450/yr, monthlyPlanId growth-plan-monthly)
   *
   * Reading them by name gets it backwards in BOTH directions: it hands a paid
   * feature to Starter customers on `growth_yearly`, and refuses it to the
   * actual annual Growth customers on `business_yearly`. Four production orgs
   * are on `growth_yearly` today. Without these entries they all fall through
   * getPlanFeatures' STARTER default, which is right for one of the three by
   * luck and wrong for the other two.
   */
  starter_yearly: LITE_FEATURES,
  growth_yearly: STARTER_FEATURES,
  business_yearly: GROWTH_FEATURES,

  // Above Growth (600 AI minutes vs Growth's 300). No production org is on it.
  'pro-team-plan-monthly': GROWTH_FEATURES,

  // ===== Outbound brand plans (gated to product='outbound' orgs) =====
  'outbound-starter-monthly': OUTBOUND_STARTER_FEATURES,
  'outbound-starter-annual': OUTBOUND_STARTER_FEATURES,
  'outbound-pro-monthly': OUTBOUND_PRO_FEATURES,
  'outbound-pro-annual': OUTBOUND_PRO_FEATURES,
  'outbound-elite-monthly': OUTBOUND_ELITE_FEATURES,
  'outbound-elite-annual': OUTBOUND_ELITE_FEATURES,
};

// Plan limits by tier
const TRIAL_LIMITS = {
  smsMonthly: 100,
  voiceMinutesMonthly: 30,
  phoneNumbers: 1,
  teamMembers: 1
};

const FREE_LIMITS = {
  smsMonthly: 50,
  voiceMinutesMonthly: 15,
  phoneNumbers: 0,
  teamMembers: 1
};

const LITE_LIMITS = {
  smsMonthly: 500,
  voiceMinutesMonthly: 100,
  phoneNumbers: 1,
  teamMembers: 1
};

const STARTER_LIMITS = {
  smsMonthly: 1000,
  voiceMinutesMonthly: 200,
  phoneNumbers: 2,
  teamMembers: 3
};

const GROWTH_LIMITS = {
  smsMonthly: 10000,
  voiceMinutesMonthly: 1000,
  phoneNumbers: 5,
  teamMembers: 10
};

const ENTERPRISE_LIMITS = {
  smsMonthly: 100000,
  voiceMinutesMonthly: 10000,
  phoneNumbers: 50,
  teamMembers: 100
};

// Outbound brand limits. High/uncapped sending is the category norm; the real
// ceiling is carrier throughput + reputation, enforced at the provider layer,
// not here. These are seat/number guardrails only.
const OUTBOUND_STARTER_LIMITS = {
  smsMonthly: 100000,
  voiceMinutesMonthly: 2000,
  phoneNumbers: 10,
  teamMembers: 2
};
const OUTBOUND_PRO_LIMITS = {
  smsMonthly: 500000,
  voiceMinutesMonthly: 10000,
  phoneNumbers: 50,
  teamMembers: 5
};
const OUTBOUND_ELITE_LIMITS = {
  smsMonthly: 2000000,
  voiceMinutesMonthly: 50000,
  phoneNumbers: 200,
  teamMembers: 10
};

// Plan limits including all plan variations
export const PLAN_LIMITS: Record<string, {
  smsMonthly: number;
  voiceMinutesMonthly: number;
  phoneNumbers: number;
  teamMembers: number;
}> = {
  // Trial/Free tiers
  trial: TRIAL_LIMITS,
  free: FREE_LIMITS,
  
  // Lite/Personal tier
  lite: LITE_LIMITS,
  'lite-plan-monthly': LITE_LIMITS,
  'lite-plan-annual': LITE_LIMITS,
  
  // Starter tier
  starter: STARTER_LIMITS,
  'starter-plan-monthly': STARTER_LIMITS,
  'starter-plan-annual': STARTER_LIMITS,
  
  // Growth tier
  growth: GROWTH_LIMITS,
  'growth-plan-monthly': GROWTH_LIMITS,
  'growth-plan-annual': GROWTH_LIMITS,
  
  // Enterprise tier
  enterprise: ENTERPRISE_LIMITS,
  'enterprise-plan-monthly': ENTERPRISE_LIMITS,
  'enterprise-plan-annual': ENTERPRISE_LIMITS,
  
  // Legacy value
  active: STARTER_LIMITS,

  // Outbound brand plans
  'outbound-starter-monthly': OUTBOUND_STARTER_LIMITS,
  'outbound-starter-annual': OUTBOUND_STARTER_LIMITS,
  'outbound-pro-monthly': OUTBOUND_PRO_LIMITS,
  'outbound-pro-annual': OUTBOUND_PRO_LIMITS,
  'outbound-elite-monthly': OUTBOUND_ELITE_LIMITS,
  'outbound-elite-annual': OUTBOUND_ELITE_LIMITS,
};

/**
 * Subscription statuses whose plan the account is entitled to right now.
 * A trial IS entitled: a Growth trial is on Growth (trial caps are applied
 * elsewhere: capPlanForTrial and the lifecycle rules in planToCapabilities).
 */
export const ENTITLING_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'trial', 'trial_active'] as const;

/**
 * The organizations.plan_type value for a subscription_plans.id.
 *
 * LimePhone tiers collapse to the bare tier ('lite' | 'starter' | 'growth' |
 * 'enterprise'); the misnamed legacy annual ids map to what they really are
 * (see PLAN_FEATURES); any other id PLAN_FEATURES knows (outbound, pro-team,
 * 'trial') is kept as-is. Null for an id we cannot place, so a caller keeps
 * the value it had rather than writing a label nothing enforces.
 */
export function planTierFromPlanId(planId: string | null | undefined): string | null {
  const id = String(planId ?? '').trim().toLowerCase();
  if (!id) return null;
  const legacy: Record<string, string> = { starter_yearly: 'lite', growth_yearly: 'starter', business_yearly: 'growth' };
  if (legacy[id]) return legacy[id];
  const m = id.match(/^(lite|starter|growth|enterprise)(?:-plan)?(?:-(?:monthly|annual|yearly))?$/);
  if (m) return m[1];
  return PLAN_FEATURES[id] ? id : null;
}

/**
 * The plan_type label to write after a subscription event, or null when none
 * should be written: the subscription is not live (only 'active' / 'trialing'
 * move the label — a past_due sub has not paid for its plan), its plan cannot
 * be placed, or the label already says it.
 */
export function planTypeToSync(
  subscriptionStatus: string | null | undefined,
  planId: string | null | undefined,
  currentPlanType: string | null | undefined,
): string | null {
  if (subscriptionStatus !== 'active' && subscriptionStatus !== 'trialing') return null;
  const tier = planTierFromPlanId(planId);
  if (!tier || tier === currentPlanType) return null;
  return tier;
}

/**
 * Subscription statuses after which the plan is no longer paid for. past_due
 * is not here: Stripe is still retrying, and recovery restores it; past grace
 * effectivePlanType already resolves it to 'free' and the sweep locks it.
 */
export const PLAN_ENDING_SUBSCRIPTION_STATUSES = ['canceled', 'cancelled', 'incomplete_expired', 'unpaid', 'incomplete'] as const;

/** Should a subscription event reset plan_type to 'free'? */
export function shouldResetPlanTypeToFree(
  subscriptionStatus: string | null | undefined,
  currentPlanType: string | null | undefined,
): boolean {
  const label = String(currentPlanType ?? '').toLowerCase();
  if (!label || label === 'free') return false;
  return (PLAN_ENDING_SUBSCRIPTION_STATUSES as readonly string[]).includes(String(subscriptionStatus ?? '').toLowerCase());
}

/**
 * The plan to enforce for an organization: its entitling subscription's plan
 * when it has one, else organizations.plan_type.
 *
 * plan_type is a denormalised label that several writers forgot to update (a
 * Growth trial stayed 'free' and was locked out of everything Growth
 * includes). The subscription row is the authority; the label is the fallback.
 */
export function effectivePlanType(
  planType: string | null | undefined,
  subscription?: { planId?: string | null; status?: string | null } | null,
  opts: {
    /**
     * The non-entitling subscription is still inside the billing timeline's
     * grace window (billingTimeline: payment failed, or trial ended unpaid,
     * less than BILLING_SUSPEND_AFTER_DAYS ago). Everything keeps working in
     * grace, so the plan does too; the suspension sweep locks it after.
     */
    inGrace?: boolean;
  } = {},
): string {
  const label = String(planType ?? '') || 'free';
  // Never had a subscription: a plan granted by hand (comp, staff, legacy)
  // lives only in the label, so the label stands.
  if (!subscription) return label;
  const status = String(subscription.status ?? '').toLowerCase();
  const entitled = (ENTITLING_SUBSCRIPTION_STATUSES as readonly string[]).includes(status)
    || (!!opts.inGrace && (GRACE_SUBSCRIPTION_STATUSES as readonly string[]).includes(status));
  if (!entitled) {
    // The subscription is what pays for a plan. Once it stops entitling
    // (trial ended unpaid, canceled, expired, unpaid past grace) a leftover
    // label must not keep granting it for free.
    return 'free';
  }
  return planTierFromPlanId(subscription.planId) ?? label;
}

/**
 * Non-entitling statuses that still carry the plan while the billing
 * timeline's grace window is open (see effectivePlanType's inGrace).
 */
export const GRACE_SUBSCRIPTION_STATUSES = ['past_due', 'unpaid', 'canceled', 'cancelled', 'incomplete_expired'] as const;

/**
 * Normalize plan type to handle any plan string from database
 * Falls back to checking the plan directly if not found
 */
function normalizePlanType(planType: string): string {
  // Return as-is - we have comprehensive coverage in PLAN_FEATURES
  return planType;
}

/**
 * Check if organization has access to a feature
 * Considers account status (trial/active) and access posture (suspended/locked)
 */
export function hasOrganizationAccess(
  lifecycle: AccountStatus,
  accessPosture: AccessPosture,
  planType: string, // Accept any string for flexibility with various plan IDs
  feature: Feature,
  trialExpired: boolean = false,
  /** Features switched on by paid add-ons. Only override the plan-feature lookup, after every base check. */
  addonFeatures: readonly string[] = [],
  /**
   * The org's plan row's metadata.featureOverrides (shared/planFeatures.ts),
   * set from the admin catalogue. Replaces the PLAN_FEATURES answer for the
   * features it names, after every posture / lifecycle / trial check. Absent
   * or empty = PLAN_FEATURES alone, exactly as before.
   */
  featureOverrides?: Partial<Record<Feature, boolean>> | null
): boolean {
  // Blocked access postures
  if (accessPosture === 'suspended' || accessPosture === 'locked') {
    return false;
  }

  // Cancelled/archived accounts have no access
  if (lifecycle === 'canceled' || lifecycle === 'archived') {
    return false;
  }

  // Restricted access posture - only billing features
  if (accessPosture === 'restricted') {
    return false; // No feature access during restrictions
  }

  // Trial expired - no access
  if (trialExpired && lifecycle === 'trialing') {
    return false;
  }

  // Check plan features for active accounts
  const normalizedPlan = normalizePlanType(planType);
  const features = PLAN_FEATURES[normalizedPlan];
  
  // If plan not found, log warning and fall back to trial features (safe default)
  if (!features) {
    console.warn(`[ACCESS] Unknown plan type: ${planType}, defaulting to starter features`);
  }
  const planDefault = features ? features.includes(feature) : !!PLAN_FEATURES['starter']?.includes(feature);
  const override = featureOverrides ? featureOverrides[feature] : undefined;
  const planHas = typeof override === 'boolean' ? override : planDefault;

  return planHas || (accessPosture === 'normal' && addonFeatures.includes(feature));
}

/**
 * Simple helper for backwards compatibility - assumes active account
 */
export function hasFeatureAccess(planType: string, feature: Feature): boolean {
  const features = PLAN_FEATURES[planType];
  if (!features) {
    console.warn(`[ACCESS] Unknown plan type: ${planType}, defaulting to starter features`);
    return PLAN_FEATURES['starter']?.includes(feature) || false;
  }
  return features.includes(feature);
}

/**
 * Get all features for a plan
 */
export function getPlanFeatures(planType: string): Feature[] {
  return PLAN_FEATURES[planType] || PLAN_FEATURES['starter'] || [];
}

/**
 * Get plan limits
 */
export function getPlanLimits(planType: string) {
  return PLAN_LIMITS[planType] || PLAN_LIMITS['trial'];
}

/**
 * Check if trial has expired
 */
export function isTrialExpired(trialExpiresAt: Date | string | null): boolean {
  if (!trialExpiresAt) return false;
  return new Date(trialExpiresAt) < new Date();
}

/**
 * Legacy function for backwards compatibility
 */
export function organizationHasFeature(organizationPlan: string, feature: Feature): boolean {
  return hasFeatureAccess(organizationPlan, feature);
}
/**
 * The access posture to actually enforce, given both fields that claim to say
 * whether an account is live.
 *
 * `organizations.status` and `organizations.access_posture` are two columns
 * describing one fact, and several admin paths wrote only the first. The gate
 * read only the second, so an organization could sit at status='suspended' and
 * keep full access — which is what every suspended organization on production
 * was doing when this was found (five of them, all access_posture='normal').
 *
 * The write paths now set both. This exists because they might not, and a
 * disagreement between two fields about whether an account is suspended must
 * resolve to suspended. Restoring access takes an explicit reactivate, which
 * clears both.
 */
export function effectiveAccessPosture(
  status: string | null | undefined,
  accessPosture: AccessPosture | string | null | undefined,
): AccessPosture {
  if (status === 'suspended' || status === 'canceled') return 'suspended';
  const posture = (accessPosture || 'normal') as AccessPosture;
  return posture;
}
